import React, { useState, useCallback, useEffect } from "react";
import { DownloadToast, DownloadNotification } from "./DownloadToast";

// Local types for download notifications (not part of core services anymore)
interface DownloadProgress {
  currentFile?: string;
  downloaded: number;
  total: number;
}

interface DownloadRetryInfo {
  filename: string;
  attempt: number;
  maxAttempts: number;
  error: string;
}

interface DownloadNotificationManagerProps {
  className?: string;
}

// Global notification manager
class NotificationManager {
  private static instance: NotificationManager;
  private listeners: ((notifications: DownloadNotification[]) => void)[] = [];
  private notifications: DownloadNotification[] = [];

  static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  addNotification(notification: Omit<DownloadNotification, 'id'>): string {
    const id = `download-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: DownloadNotification = { ...notification, id };
    
    this.notifications.push(newNotification);
    this.notifyListeners();
    
    return id;
  }

  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notifyListeners();
  }

  updateNotification(id: string, updates: Partial<DownloadNotification>): void {
    const index = this.notifications.findIndex(n => n.id === id);
    if (index !== -1) {
      this.notifications[index] = { ...this.notifications[index], ...updates };
      this.notifyListeners();
    }
  }

  subscribe(listener: (notifications: DownloadNotification[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }

  // Convenience methods for common notification types
  notifyDownloadStarted(filename: string, strategy?: string): string {
    return this.addNotification({
      type: 'downloading',
      message: `Downloading ${filename}...`,
      filename,
      autoHide: false
    });
  }

  notifyDownloadProgress(progress: DownloadProgress): string | void {
    const existingId = this.notifications.find(n => 
      n.type === 'downloading' && n.message.includes('files')
    )?.id;

    if (existingId) {
      this.updateNotification(existingId, {
        message: `Downloading ${progress.currentFile || 'files'}...`,
        filename: progress.currentFile,
        progress: progress.downloaded,
        total: progress.total
      });
    } else {
      return this.addNotification({
        type: 'downloading',
        message: `Downloading ${progress.total} files...`,
        filename: progress.currentFile,
        progress: progress.downloaded,
        total: progress.total,
        autoHide: false
      });
    }
  }

  notifyDownloadRetry(retry: DownloadRetryInfo): string {
    return this.addNotification({
      type: 'retry',
      message: `Retrying download (${retry.attempt}/${retry.maxAttempts})`,
      filename: retry.filename,
      autoHide: false
    });
  }

  notifyDownloadSuccess(filename: string): string {
    return this.addNotification({
      type: 'success',
      message: `Downloaded successfully`,
      filename,
      autoHide: true
    });
  }

  notifyDownloadError(filename: string, error?: string): string {
    return this.addNotification({
      type: 'error',
      message: error || 'Download failed',
      filename,
      autoHide: false
    });
  }
}

export const downloadNotificationManager = NotificationManager.getInstance();

export const DownloadNotificationManager: React.FC<DownloadNotificationManagerProps> = ({
  className = "",
}) => {
  const [notifications, setNotifications] = useState<DownloadNotification[]>([]);

  useEffect(() => {
    const unsubscribe = downloadNotificationManager.subscribe(setNotifications);
    return unsubscribe;
  }, []);

  const handleDismiss = useCallback((id: string) => {
    downloadNotificationManager.removeNotification(id);
  }, []);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className={`fixed top-4 right-4 z-50 space-y-2 max-w-sm ${className}`}>
      {notifications.map((notification) => (
        <DownloadToast
          key={notification.id}
          notification={notification}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
};